import React, { memo, useEffect, useMemo, useState } from "react";
import {
  BugOutlined,
  CheckSquareOutlined,
  DeleteOutlined,
  DislikeFilled,
  DislikeOutlined,
  EditOutlined,
  FileOutlined,
  LikeFilled,
  LikeOutlined,
  MoreOutlined,
  SmileOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import data from "@emoji-mart/data";
import i18n from "@emoji-mart/data/i18n/pl.json";
import Picker from "@emoji-mart/react";
import { Avatar, Button, Dropdown, Input, Popconfirm, Popover, Space, Tooltip, Typography, type MenuProps } from "antd";
import dayjs from "dayjs";
import { type BaseEmoji } from "emoji-mart/dist-es/utils/emoji-index/nimble-emoji-index";
import { useTranslation } from "react-i18next";
import Linkify from "linkify-react";

import { withoutLinkPreview } from "@fitness-app/client-shared/features/chat/link-preview";
import { PermissionKey, UserRole } from "@fitness-app/data-models";
import {
  MessageInContext,
  MessageType,
  type ChatMember,
  type ChatMessage as IChatMessage,
  type LinkPreview,
  type MessageAgentReplyFeedbackVote,
} from "@fitness-app/data-models/entities/Chat";
import { getUserInitials } from "@fitness-app/utils";
import { getChatMemberName } from "@fitness-app/utils/src/chat/nameUtils";
import { cn } from "@fitness-app/utils/src/styles/cn";

import FilesList from "~/components/Upload/FilesList";
import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";
import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useOuterClick } from "~/hooks/useOuterClick";
import { useChatMessageEditContext } from "~/modules/Chat/components/ChatMessageEditContext";
import ChatMessageReactions from "~/modules/Chat/components/ChatMessagesList/ChatMessageReactions";
import { LinkPreviews } from "~/modules/Chat/components/ChatMessagesList/LinkPreviewCard";
import { useChatAgentSuggestionAction } from "~/modules/Chat/hooks/useChatAgentSuggestionAction";
import { useUpdateChatMessage } from "~/modules/Chat/hooks/useUpdateChatMessage";
import { useChatUtilitiesContext } from "~/modules/Chat/providers/ChatUtilitiesProvider";

interface ChatMessageProps {
  incomingMessage: boolean;
  authorData?: ChatMember;
  channelId: string;
  message: IChatMessage;
  shouldRenderMetaTag: boolean;
  authorsById: Record<string, ChatMember>;
  /** Viewer is not assigned to the channel — hide every action RLS would reject (TRE-1714). */
  readOnly?: boolean;
}

export const renderDate = (timestamp: string) => dayjs(timestamp).locale("pl").format("LT");

const ChatMessage = ({
  message,
  incomingMessage,
  shouldRenderMetaTag,
  authorData,
  authorsById,
  readOnly,
}: ChatMessageProps): React.ReactElement => {
  const { isClient, userId } = useUserRole();
  const canWriteChat = usePermission(PermissionKey.CHAT_WRITE);
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  const [openEmojiPicker, toggleEmojiPicker] = useState(false);
  const updateMessage = useUpdateChatMessage();
  const innerRef = useOuterClick(openEmojiPicker ? () => toggleEmojiPicker(false) : undefined, 1);
  const { setNoteModel, setTaskModel, setSupportRequestModel } = useChatUtilitiesContext();
  const { setMessageToEdit } = useChatMessageEditContext();
  const [videoLink, setVideoLink] = useState<string | null>(null);
  const acceptSuggestionMutation = useChatAgentSuggestionAction("accept");
  const dismissSuggestionMutation = useChatAgentSuggestionAction("dismiss");
  const feedbackSuggestionMutation = useChatAgentSuggestionAction("feedback");
  const regenerateSuggestionMutation = useChatAgentSuggestionAction("regenerate");

  const onEmojiSelect = (emoji: BaseEmoji) => {
    toggleEmojiPicker(false);

    if (message.reactions?.find((reaction) => reaction.icon === emoji.native && reaction.authorId === userId)) {
      updateMessage({
        id: message.id,
        channelId: message.channelId,
        authorId: message.authorId,
        reactions: message.reactions.filter((r) => !(r.icon === emoji.native && r.authorId === userId)),
      });
    } else {
      updateMessage({
        id: message.id,
        authorId: message.authorId,
        channelId: message.channelId,
        reactions: [
          ...(message.reactions ?? []),
          {
            iconCode: emoji.unified,
            addedAt: dayjs().unix(),
            authorId: userId,
            icon: emoji.native,
            iconName: emoji.id || emoji.name,
          },
        ],
      });
    }
  };

  const { t } = useTranslation(["chat", "common"]);
  const clientId = authorsById[message.authorId]?.clientId;
  const suggestionOwner = message.metadata?.agentReplyOwnerTeamMemberId
    ? authorsById[message.metadata.agentReplyOwnerTeamMemberId]
    : null;
  const [isEditingSuggestion, setIsEditingSuggestion] = useState(false);
  const [suggestionDraft, setSuggestionDraft] = useState(message.metadata?.agentReplyText || "");
  const [openFeedbackPopoverVote, setOpenFeedbackPopoverVote] = useState<MessageAgentReplyFeedbackVote | null>(null);
  const [suggestionFeedbackVote, setSuggestionFeedbackVote] = useState<MessageAgentReplyFeedbackVote | null>(
    message.metadata?.agentReplyFeedbackVote || null,
  );
  const [suggestionFeedbackComment, setSuggestionFeedbackComment] = useState(
    message.metadata?.agentReplyFeedbackComment || "",
  );
  const shouldRenderSuggestionCard =
    canWriteChat &&
    canUseAgents &&
    !readOnly &&
    incomingMessage &&
    message.authorRole === UserRole.CLIENT &&
    message.metadata?.agentReplyStatus === "suggested" &&
    Boolean(message.metadata.agentReplyText);
  const isSuggestionActionPending =
    acceptSuggestionMutation.isLoading ||
    dismissSuggestionMutation.isLoading ||
    feedbackSuggestionMutation.isLoading ||
    regenerateSuggestionMutation.isLoading;
  const trimmedSuggestionDraft = suggestionDraft.trim();
  const hasSavedSuggestionFeedback = Boolean(message.metadata?.agentReplyFeedbackSubmittedAt);
  const savedSuggestionFeedbackVote = message.metadata?.agentReplyFeedbackVote || null;
  const savedSuggestionFeedbackComment = message.metadata?.agentReplyFeedbackComment?.trim() || "";
  const hasSuggestionFeedbackChanges =
    suggestionFeedbackVote !== (message.metadata?.agentReplyFeedbackVote || null) ||
    suggestionFeedbackComment !== (message.metadata?.agentReplyFeedbackComment || "");

  useEffect(() => {
    setSuggestionDraft(message.metadata?.agentReplyText || "");
    setIsEditingSuggestion(false);
    setOpenFeedbackPopoverVote(null);
    setSuggestionFeedbackVote(message.metadata?.agentReplyFeedbackVote || null);
    setSuggestionFeedbackComment(message.metadata?.agentReplyFeedbackComment || "");
  }, [
    message.id,
    message.metadata?.agentReplyStatus,
    message.metadata?.agentReplyText,
    message.metadata?.agentReplyFeedbackVote,
    message.metadata?.agentReplyFeedbackComment,
  ]);

  const handleMessageDeleting = () => {
    updateMessage({
      id: message.id,
      channelId: message.channelId,
      authorId: message.authorId,
      deleted: true,
    });
  };

  // Author only, like editing: RLS lets every channel member update the row (reactions).
  const onRemoveLinkPreview =
    !incomingMessage && !readOnly
      ? (preview: LinkPreview) =>
          updateMessage({
            id: message.id,
            channelId: message.channelId,
            authorId: message.authorId,
            extra: withoutLinkPreview(message.extra, preview.url),
          })
      : undefined;

  const handleSuggestionEditCancel = () => {
    setSuggestionDraft(message.metadata?.agentReplyText || "");
    setIsEditingSuggestion(false);
  };

  const handleSuggestionAccept = () => {
    acceptSuggestionMutation.mutate({
      messageId: message.id,
      replyText: suggestionDraft,
    });
  };

  const handleSuggestionFeedbackSubmit = () => {
    if (!suggestionFeedbackVote) {
      return;
    }

    feedbackSuggestionMutation.mutate(
      {
        messageId: message.id,
        feedbackVote: suggestionFeedbackVote,
        feedbackComment: suggestionFeedbackComment,
      },
      {
        onSuccess: () => setOpenFeedbackPopoverVote(null),
      },
    );
  };

  const handleSuggestionFeedbackPopoverOpenChange = (vote: MessageAgentReplyFeedbackVote, open: boolean) => {
    if (open) {
      setSuggestionFeedbackVote(vote);
      setOpenFeedbackPopoverVote(vote);
      return;
    }

    if (openFeedbackPopoverVote === vote) {
      setOpenFeedbackPopoverVote(null);
    }
  };

  const handleOpenSavedSuggestionFeedback = () => {
    const voteToOpen = savedSuggestionFeedbackVote || suggestionFeedbackVote || "up";

    setSuggestionFeedbackVote(voteToOpen);
    setOpenFeedbackPopoverVote(voteToOpen);
  };

  const suggestionSecondaryActionItems: MenuProps["items"] = [
    {
      key: "edit",
      label: t("common:button.edit"),
      disabled: isSuggestionActionPending || isEditingSuggestion,
      onClick: () => setIsEditingSuggestion(true),
    },
    {
      key: "regenerate",
      label: t("agentSuggestion.regenerate"),
      disabled: isSuggestionActionPending,
      onClick: () => regenerateSuggestionMutation.mutate({ messageId: message.id }),
    },
  ];

  const renderSuggestionFeedbackPopoverContent = (vote: MessageAgentReplyFeedbackVote) => (
    <div className="w-[300px]">
      <Typography.Text className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("agentSuggestion.feedbackTitle")}
      </Typography.Text>
      <Typography.Text className="mb-2 block text-sm font-medium text-slate-800">
        {vote === "up" ? t("agentSuggestion.feedbackPositive") : t("agentSuggestion.feedbackNegative")}
      </Typography.Text>
      <Input.TextArea
        value={suggestionFeedbackComment}
        onChange={(event) => setSuggestionFeedbackComment(event.target.value)}
        autoSize={{ minRows: 2, maxRows: 4 }}
        disabled={isSuggestionActionPending}
        placeholder={t<string>("agentSuggestion.feedbackCommentPlaceholder")}
        className="mb-2"
      />
      <Space size="small" wrap>
        <Button
          size="small"
          type="primary"
          loading={feedbackSuggestionMutation.isLoading}
          disabled={isSuggestionActionPending || !suggestionFeedbackVote || !hasSuggestionFeedbackChanges}
          onClick={handleSuggestionFeedbackSubmit}
        >
          {hasSavedSuggestionFeedback ? t("agentSuggestion.feedbackUpdate") : t("agentSuggestion.feedbackSave")}
        </Button>
        {hasSavedSuggestionFeedback ? (
          <Typography.Text className="text-xs text-slate-500">{t("agentSuggestion.feedbackSaved")}</Typography.Text>
        ) : null}
      </Space>
    </div>
  );

  const items: MenuProps["items"] = useMemo(
    () => [
      {
        key: "reaction",
        label: "Dodaj reakcję",
        icon: <SmileOutlined />,
        onClick: () => toggleEmojiPicker(true),
      },
      ...(incomingMessage
        ? clientId
          ? [
              {
                key: "note",
                label: "Utwórz notatkę",
                icon: <FileOutlined />,
                onClick: () =>
                  setNoteModel({
                    traineeId: clientId,
                    content:
                      message.content || message.metadata?.transcription
                        ? `Treść wiadomości: ${message.content || message.metadata?.transcription}`
                        : `Załączniki wysłane przez klienta ${dayjs(message.createdAt).format("HH:mm DD.MM.YYYY")}`,
                    attachments: message.files
                      ? message.files.map((file) => ({
                          url: file.url,
                          name: file.originalName || file.originalName || file.uid,
                          uid: file.uid,
                          contentType: file.contentType,
                          size: file.size,
                          protected: true,
                        }))
                      : [],
                  }),
              },
              {
                key: "task",
                label: "Utwórz zadanie",
                icon: <CheckSquareOutlined />,
                onClick: () => {
                  const author = authorsById[message.authorId];
                  setTaskModel({
                    traineeId: clientId,
                    trainee: {
                      id: clientId,
                      firstName: author?.firstName || author?.name || "",
                      lastName: author?.lastName || null,
                      email: author?.email || null,
                    },
                    title: `Odpowiedz: ${author?.firstName || "Klient"}`,
                    description: message.content || message.metadata?.transcription || undefined,
                  });
                },
              },
              ...(setSupportRequestModel
                ? [
                    {
                      key: "support",
                      label: "Utwórz zgłoszenie",
                      icon: <BugOutlined />,
                      onClick: () => {
                        const author = authorsById[message.authorId];
                        const authorName = [author?.firstName || author?.name, author?.lastName]
                          .filter(Boolean)
                          .join(" ");
                        const content = message.content || message.metadata?.transcription;
                        setSupportRequestModel({
                          trainee: {
                            id: clientId,
                            label: [authorName, author?.email ? `(${author.email})` : ""].filter(Boolean).join(" "),
                          },
                          clientEmail: author?.email || undefined,
                          description: content ? `Wiadomość klienta z czatu:\n${content}` : undefined,
                          metadata: { chatMessageId: message.id, chatChannelId: message.channelId },
                        });
                      },
                    },
                  ]
                : []),
            ]
          : []
        : [
            {
              key: "edit",
              label: "Edytuj wiadomość",
              icon: <EditOutlined />,
              onClick: () => setMessageToEdit(message),
            },
            {
              key: "remove",
              danger: true,
              label: (
                <Popconfirm
                  title="Czy na pewno chcesz usunąć tę wiadomość?"
                  arrow={false}
                  okButtonProps={{ danger: true }}
                  description="Ta operacja jest nieodwracalna."
                  onConfirm={() => handleMessageDeleting()}
                >
                  <span>Usuń wiadomość</span>
                </Popconfirm>
              ),
              icon: <DeleteOutlined />,
            },
          ]),
    ],
    [incomingMessage, message, clientId, authorsById, setTaskModel, setSupportRequestModel],
  );
  const linkifyOptions = { target: "_blank", rel: "noopener noreferrer" };
  const isVoiceMessage = message.files?.length === 1 && Boolean(message.files[0]?.contentType?.startsWith("audio"));

  const renderMessage = () => {
    if (
      message.type == MessageType.WithContext &&
      message.context &&
      [MessageInContext.AddDishComment, MessageInContext.UpdateDishComment].includes(message.context.type)
    ) {
      return (
        <div
          className="relative mx-3 mb-1 flex flex-col rounded-md border border-dashed bg-blue-300/40 p-2"
          id="chat-message"
        >
          <p className="mb-1 text-xs font-medium   text-gray-500 underline">
            {t(`contextType.${message.context.type}`)}
          </p>
          {<div className="text-xs font-semibold">Dodano do posiłku {message.context.metadata.dishName}:</div>}
          <div
            className={cn(
              "wh m-1 my-2 ml-8 inline-block w-full whitespace-pre-line break-words rounded-md bg-gray-200 p-1 px-2 text-left font-medium leading-snug",
              incomingMessage && "ml-0 bg-blue-300 text-white",
              !message.content && "hidden",
            )}
          >
            <Linkify options={linkifyOptions}>{message.content}</Linkify>
          </div>
          {message.files?.length ? (
            <div className="w-full">
              <FilesList
                files={message.files}
                readonly
                position={incomingMessage ? "left" : "right"}
                showOnlyFirstAudio
              />
            </div>
          ) : null}
          <LinkPreviews
            message={message}
            position={incomingMessage ? "left" : "right"}
            onRemove={onRemoveLinkPreview}
          />

          {message.metadata?.transcription ? (
            <div className={"mb-2 mr-7 text-right"}>
              <Tooltip title={message.metadata?.transcription}>
                <Typography.Link>{message.metadata.summary || "Pokaż transkrypcję"}</Typography.Link>
              </Tooltip>
            </div>
          ) : null}
        </div>
      );
    }
    if (message.type == MessageType.WithContext && message.context.type === MessageInContext.AddRecordingFeedback) {
      return (
        <div
          className={cn("relative mx-3 mb-1 flex flex-col rounded-md border border-dashed bg-blue-300/40 p-2")}
          id="chat-message"
        >
          <YoutubeOutlined
            className="text-green-500"
            style={{ fontSize: 24 }}
            onClick={() => setVideoLink(String(message.context.metadata.url))}
          />
          <p className="mb-1 text-xs font-medium text-gray-600 underline">
            {t(`contextType.${message.context.type}`)}:
          </p>
          {
            <div className="text-xs font-semibold">
              {message.context.metadata.exerciseName} w dniu {message.context.metadata.exerciseDate}
            </div>
          }
          <div
            className={cn(
              "wh m-1 my-2 ml-8 inline-block whitespace-pre-line break-words rounded-md bg-gray-300/80 p-1 px-2 text-left font-medium leading-snug",
              incomingMessage && "bg-blue-300 text-white",
              !message.content && "hidden",
            )}
          >
            <Linkify options={linkifyOptions}>{message.content}</Linkify>
          </div>
          {message.files?.length ? (
            <div className="w-full">
              <FilesList
                files={message.files}
                readonly
                position={incomingMessage ? "left" : "right"}
                showOnlyFirstAudio
              />
            </div>
          ) : null}
          <LinkPreviews
            message={message}
            position={incomingMessage ? "left" : "right"}
            onRemove={onRemoveLinkPreview}
          />

          {message.metadata?.transcription ? (
            <div className={"mb-2 mr-7 text-right"}>
              <Tooltip title={message.metadata?.transcription}>
                <Typography.Link>{message.metadata.summary || "Pokaż transkrypcję"}</Typography.Link>
              </Tooltip>
            </div>
          ) : null}
          <VideoPlayer visible={!!videoLink} videoUrl={videoLink} onClose={() => setVideoLink(null)} />
        </div>
      );
    }
    if (message.type == MessageType.WithContext && message.context) {
      return (
        <div
          className={cn("relative mx-3 mb-1 flex flex-col rounded-md border border-dashed bg-blue-300/40 p-2")}
          id="chat-message"
        >
          <p className="mb-1 text-xs font-medium text-gray-600 underline">
            {t(`contextType.${message.context.type}`)}:
          </p>
          {
            <div className="text-xs font-semibold">
              {message.context.metadata.exerciseName} w dniu {message.context.metadata.exerciseDate}
            </div>
          }
          <div
            className={cn(
              "wh m-1 my-2 ml-8 inline-block whitespace-pre-line break-words rounded-md bg-gray-300/80 p-1 px-2 text-left font-medium leading-snug",
              incomingMessage && "bg-blue-300 text-white",
              !message.content && "hidden",
            )}
          >
            <Linkify options={linkifyOptions}>{message.content}</Linkify>
          </div>
          {message.files?.length ? (
            <div className="w-full">
              <FilesList
                files={message.files}
                readonly
                position={incomingMessage ? "left" : "right"}
                showOnlyFirstAudio
              />
            </div>
          ) : null}
          <LinkPreviews
            message={message}
            position={incomingMessage ? "left" : "right"}
            onRemove={onRemoveLinkPreview}
          />

          {message.metadata?.transcription ? (
            <div className={"mb-2 mr-7 text-right"}>
              <Tooltip title={message.metadata?.transcription}>
                <Typography.Link>{message.metadata.summary || "Pokaż transkrypcję"}</Typography.Link>
              </Tooltip>
            </div>
          ) : null}
        </div>
      );
    }
    return (
      <div className="group relative flex w-full flex-col" id="chat-message">
        <div className={incomingMessage ? "relative min-w-[50px] self-start " : "relative min-w-[50px] self-end "}>
          {readOnly ? null : (
            <div
              className={cn(
                "invisible absolute z-50 flex cursor-pointer items-center justify-center group-hover:visible",
                // A voice message usually has no text, so the bubble row is empty and the trigger
                // used to sit on the player's top edge. The player renders inside this wrapper
                // for that case; anchor to its 60 px band instead of the whole stack.
                isVoiceMessage ? "bottom-0 h-[60px]" : "inset-y-0",
                incomingMessage ? "right-3" : "left-3",
              )}
            >
              <Popover
                overlayInnerStyle={{ boxShadow: "none", padding: 0 }}
                trigger="focus"
                destroyTooltipOnHide
                open={openEmojiPicker}
                arrow={false}
                onOpenChange={toggleEmojiPicker}
                content={
                  <div ref={innerRef}>
                    <Picker data={data} onEmojiSelect={onEmojiSelect} i18n={i18n} />
                  </div>
                }
              >
                <Dropdown menu={{ items }} placement={incomingMessage ? "topLeft" : "topRight"}>
                  <MoreOutlined className="rotate-90 text-gray-600" style={{ fontSize: 26 }} />
                </Dropdown>
              </Popover>
            </div>
          )}

          <div className="px-3">
            <div
              className={cn(
                "overflow-wrap my-0.5 mb-1 inline-block w-auto whitespace-pre-line break-words rounded-md bg-gray-200 p-1 px-2 text-left font-medium leading-snug [&>a]:break-all",
                isClient && "bg-white",
                incomingMessage ? "ml-0 mr-8 bg-blue-400 text-white" : "ml-8 mr-0",
                !message.content && "hidden",
              )}
            >
              <Linkify options={linkifyOptions}>{message.content}</Linkify>
            </div>
          </div>

          {/* Same 32 px gutter the text bubble keeps (ml-8 / mr-8): the options trigger lives in it. */}
          {isVoiceMessage ? (
            <div className={incomingMessage ? "mr-8" : "ml-8"}>
              <FilesList
                files={message.files}
                readonly
                position={incomingMessage ? "left" : "right"}
                showOnlyFirstAudio
              />
            </div>
          ) : null}
        </div>

        {message.files?.length && !isVoiceMessage ? (
          <FilesList files={message.files} readonly position={incomingMessage ? "left" : "right"} showOnlyFirstAudio />
        ) : null}

        <LinkPreviews message={message} position={incomingMessage ? "left" : "right"} onRemove={onRemoveLinkPreview} />

        {message.metadata?.transcription ? (
          <div className={"mb-2 mr-7 text-right"}>
            <Tooltip title={message.metadata.transcription}>
              <Typography.Link>{message.metadata.summary || "Pokaż transkrypcję"}</Typography.Link>
            </Tooltip>
          </div>
        ) : null}

        <ChatMessageReactions
          position={incomingMessage ? "left" : "right"}
          incomingMessage={incomingMessage}
          reactions={message.reactions}
          messageId={message.id}
          authorsById={authorsById}
          authorId={message.authorId}
          channelId={message.channelId}
          readOnly={readOnly}
        />

        {shouldRenderSuggestionCard ? (
          <div className="px-3">
            <div className="mt-2 max-w-[420px] rounded-2xl border border-slate-200 bg-white p-3.5 text-slate-900 shadow-[0_12px_32px_rgba(15,23,42,0.08)]">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <div className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">
                    {t("agentSuggestion.title")}
                  </div>
                  {suggestionOwner ? (
                    <div className="mt-2 text-xs text-slate-500">
                      {t("agentSuggestion.generatedBy", {
                        name: getChatMemberName(suggestionOwner),
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
              {isEditingSuggestion ? (
                <div className="mb-3 space-y-2">
                  <Input.TextArea
                    value={suggestionDraft}
                    onChange={(event) => setSuggestionDraft(event.target.value)}
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    disabled={isSuggestionActionPending}
                  />
                  <div className="flex justify-end">
                    <Button
                      size="small"
                      className="!border-slate-300 !bg-white !text-slate-700 !shadow-none hover:!border-slate-400 hover:!text-slate-900"
                      disabled={isSuggestionActionPending}
                      onClick={handleSuggestionEditCancel}
                    >
                      {t("common:button.cancel")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <Typography.Paragraph className="mb-0 whitespace-pre-line text-sm leading-6 text-slate-700">
                    {message.metadata?.agentReplyText}
                  </Typography.Paragraph>
                </div>
              )}
              <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50/90 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <Typography.Text className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t("agentSuggestion.feedbackTitle")}
                    </Typography.Text>
                    {hasSavedSuggestionFeedback ? (
                      <div className="mt-2 space-y-2">
                        <div
                          className={cn(
                            "inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium",
                            savedSuggestionFeedbackVote === "up"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-rose-200 bg-rose-50 text-rose-700",
                          )}
                        >
                          {savedSuggestionFeedbackVote === "up" ? <LikeFilled /> : <DislikeFilled />}
                          {savedSuggestionFeedbackVote === "up"
                            ? t("agentSuggestion.feedbackPositive")
                            : t("agentSuggestion.feedbackNegative")}
                        </div>
                        {savedSuggestionFeedbackComment ? (
                          <Typography.Paragraph className="mb-0 whitespace-pre-line break-words text-xs leading-5 text-slate-600">
                            {savedSuggestionFeedbackComment}
                          </Typography.Paragraph>
                        ) : (
                          <Typography.Text className="text-xs text-slate-500">
                            {t("agentSuggestion.feedbackSaved")}
                          </Typography.Text>
                        )}
                      </div>
                    ) : (
                      <Typography.Text className="mt-2 block text-xs text-slate-500">
                        {t("agentSuggestion.feedbackHint")}
                      </Typography.Text>
                    )}
                  </div>
                  {hasSavedSuggestionFeedback ? (
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      className="!border-slate-300 !bg-white !text-slate-600 !shadow-none hover:!border-slate-400 hover:!text-slate-800"
                      onClick={handleOpenSavedSuggestionFeedback}
                    >
                      {t("agentSuggestion.feedbackEdit")}
                    </Button>
                  ) : null}
                </div>
                <Space size="small" wrap className="mt-3">
                  <Popover
                    trigger="click"
                    placement="top"
                    open={openFeedbackPopoverVote === "up"}
                    onOpenChange={(open) => handleSuggestionFeedbackPopoverOpenChange("up", open)}
                    content={renderSuggestionFeedbackPopoverContent("up")}
                  >
                    <Button
                      size="small"
                      className={cn(
                        "!rounded-full !shadow-none",
                        suggestionFeedbackVote === "up"
                          ? "!border-emerald-600 !bg-emerald-600 !text-white"
                          : "!border-slate-300 !bg-white !text-slate-700",
                      )}
                      disabled={isSuggestionActionPending}
                      icon={suggestionFeedbackVote === "up" ? <LikeFilled /> : <LikeOutlined />}
                    >
                      {t("agentSuggestion.feedbackPositive")}
                    </Button>
                  </Popover>
                  <Popover
                    trigger="click"
                    placement="top"
                    open={openFeedbackPopoverVote === "down"}
                    onOpenChange={(open) => handleSuggestionFeedbackPopoverOpenChange("down", open)}
                    content={renderSuggestionFeedbackPopoverContent("down")}
                  >
                    <Button
                      size="small"
                      className={cn(
                        "!rounded-full !shadow-none",
                        suggestionFeedbackVote === "down"
                          ? "!border-rose-600 !bg-rose-600 !text-white"
                          : "!border-slate-300 !bg-white !text-slate-700",
                      )}
                      disabled={isSuggestionActionPending}
                      icon={suggestionFeedbackVote === "down" ? <DislikeFilled /> : <DislikeOutlined />}
                    >
                      {t("agentSuggestion.feedbackNegative")}
                    </Button>
                  </Popover>
                </Space>
              </div>
              <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-2">
                <Button
                  type="primary"
                  size="small"
                  className="!h-9 !rounded-xl !border-emerald-600 !bg-emerald-600 !font-medium !shadow-none hover:!border-emerald-500 hover:!bg-emerald-500"
                  loading={acceptSuggestionMutation.isLoading}
                  disabled={isSuggestionActionPending || !trimmedSuggestionDraft}
                  onClick={handleSuggestionAccept}
                >
                  {t("agentSuggestion.accept")}
                </Button>
                <div className="w-full [&_.ant-btn]:!h-9 [&_.ant-btn]:!border-slate-300 [&_.ant-btn]:!bg-white [&_.ant-btn]:!font-medium [&_.ant-btn]:!text-slate-700 [&_.ant-btn]:!shadow-none [&_.ant-dropdown-button]:!flex [&_.ant-dropdown-button]:!w-full">
                  <Dropdown.Button
                    size="small"
                    menu={{ items: suggestionSecondaryActionItems }}
                    disabled={isSuggestionActionPending}
                    loading={dismissSuggestionMutation.isLoading}
                    onClick={() => dismissSuggestionMutation.mutate({ messageId: message.id })}
                  >
                    {t("agentSuggestion.dismiss")}
                  </Dropdown.Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="flex flex-col overflow-x-hidden py-0.5"
      style={{ alignItems: incomingMessage ? "flex-start" : "flex-end" }}
    >
      {shouldRenderMetaTag || (!message.content && message.files?.length) ? (
        <Tooltip title={getChatMemberName(authorData)}>
          <div
            className={cn(
              "mx-0 mb-1 mt-1 flex cursor-pointer items-center justify-center px-3",
              !incomingMessage && "mr-2",
            )}
            style={{ flexDirection: incomingMessage ? "row" : "row-reverse" }}
          >
            <Avatar
              size={26}
              className="mx-1"
              src={authorData?.avatarUrl}
              gap={6}
              style={
                authorData?.avatarUrl
                  ? undefined
                  : {
                      backgroundColor: authorData?.role === UserRole.CLIENT ? "rgb(247, 194, 93)" : "rgb(24, 26, 101)",
                    }
              }
              icon={!authorData ? <DeleteOutlined /> : undefined}
            >
              {authorData ? getUserInitials(authorData) : "Deleted"}
            </Avatar>
            {!authorData || (authorData.disabled && !isClient) ? (
              <span className="text-xs text-gray-500" style={{ marginRight: 5 }}>
                {t("inactive")}
              </span>
            ) : null}
            <span className="text-xs text-gray-500">{renderDate(message.createdAt)}</span>
          </div>
        </Tooltip>
      ) : null}
      {renderMessage()}
    </div>
  );
};

export default memo(ChatMessage);
